import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useRef } from "react";
import {
  resolveSidebarWidthPx,
  type SidebarLayoutMode,
  type SidebarLayoutVariant,
} from "@/components/layout/sidebarLayout.const";

export type LayoutDensity = "normal" | "compact";

export type ResponsiveSidebarLayout = {
  /** Collapsed icon rail, compact expanded, or full expanded. */
  mode: SidebarLayoutMode;
  /** Resolved navbar width in pixels for AppShell. */
  sidebarWidthPx: number;
  /** True when the desktop sidebar shows labels (not the icon rail). Always false below `sm`. */
  sidebarExpanded: boolean;
  /** True when nav items should show labels — desktop expanded, or the mobile hamburger overlay. */
  showNavLabels: boolean;
  /** Typography/spacing density for sidebar nav and page chrome. */
  layoutDensity: LayoutDensity;
  /** NavLink label font size. */
  navFontSize: string | number;
  /** Navbar horizontal padding Mantine token. */
  navPaddingX: "xs" | "sm" | "md";
  /** True at or above Mantine `lg` (~1200px). */
  isWideDesktop: boolean;
  /** True at or above Mantine `sm` (~768px) — desktop sidebar band. */
  isDesktop: boolean;
};

type Options = {
  variant: SidebarLayoutVariant;
  /** User preference from usePersistedSidebarOpen (ignored on mobile). */
  desktopOpen: boolean;
  /** Called when viewport shrinks below `lg` to auto-collapse. */
  closeDesktop?: () => void;
};

/**
 * Three-mode sidebar: collapsed | compact-expanded (below lg) | full-expanded (lg+).
 * Auto-collapses when viewport crosses from wide to narrow desktop.
 */
export function useResponsiveSidebarLayout({
  variant,
  desktopOpen,
  closeDesktop,
}: Options): ResponsiveSidebarLayout {
  const theme = useMantineTheme();
  const isWideDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, true, {
    getInitialValueInEffect: true,
  });
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, true, {
    getInitialValueInEffect: true,
  });

  const wasWideDesktopRef = useRef<boolean | null>(null);

  useEffect(() => {
    // First measurement is hydration of `getInitialValueInEffect`, not a real
    // wide→narrow resize. Closing here persisted a collapsed "drawer" sidebar.
    if (wasWideDesktopRef.current === null) {
      wasWideDesktopRef.current = isWideDesktop;
      return;
    }
    if (wasWideDesktopRef.current && !isWideDesktop && isDesktop && desktopOpen && closeDesktop) {
      closeDesktop();
    }
    wasWideDesktopRef.current = isWideDesktop;
  }, [isWideDesktop, isDesktop, desktopOpen, closeDesktop]);

  return useMemo(() => {
    const sidebarExpanded = isDesktop && desktopOpen;
    let mode: SidebarLayoutMode = "collapsed";
    if (sidebarExpanded) {
      mode = isWideDesktop ? "full-expanded" : "compact-expanded";
    }

    const layoutDensity: LayoutDensity = mode === "compact-expanded" ? "compact" : "normal";
    const navFontSize = mode === "compact-expanded" ? 13 : mode === "full-expanded" ? 14 : 14;
    const showNavLabels = Boolean(sidebarExpanded) || !isDesktop;
    const navPaddingX = mode === "compact-expanded" ? "sm" : showNavLabels ? "md" : "xs";

    return {
      mode,
      sidebarWidthPx: resolveSidebarWidthPx(mode, variant),
      sidebarExpanded: Boolean(sidebarExpanded),
      showNavLabels,
      layoutDensity,
      navFontSize,
      navPaddingX,
      isWideDesktop: Boolean(isWideDesktop),
      isDesktop: Boolean(isDesktop),
    };
  }, [isDesktop, isWideDesktop, desktopOpen, variant]);
}
